import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import { OrderProductPreview, PlanInterval } from '../../../models';

export const PreviewedProductListing = (
  { productsPreview }: { productsPreview: OrderProductPreview[] },
) => {
  const intl = useIntl();

  const renderProductPriceDetails = (product: OrderProductPreview) => {
    // when discount quantity is less than quantity,
    // it means that the product is not fully discounted
    // so we need separate the unit amount into two parts
    if (product.discountQuantity > 0 && product.discountQuantity < product.newQuantity) {
      const discountedTotal = (
        product.unitAmount * product.discountQuantity) - product.discountAmount;
      const discountedUnitAmount = (discountedTotal) / product.discountQuantity;

      return (
        <Box display="flex" flexDirection="column">
          <Typography variant="subtitle1" fontWeight={500} fontSize={12}>
            <FormattedMessage
              id="page.expand.plan.price.summary.product.details"
              values={{
                unitAmount: discountedUnitAmount.toLocaleString('en-us'),
                quantity: product.discountQuantity,
                unit: intl.formatMessage({ id: `feature.${product.feature.toLowerCase()}.unit.plural` }),
                duration: PlanInterval.getCycle(product.billingInterval),
              }}
            />
          </Typography>
          <Typography variant="subtitle1" fontWeight={500} fontSize={12} paddingBottom={1}>
            <FormattedMessage
              id="page.expand.plan.price.summary.product.details"
              values={{
                unitAmount: product.unitAmount.toLocaleString('en-us'),
                quantity: product.newQuantity - product.discountQuantity,
                unit: intl.formatMessage({ id: `feature.${product.feature.toLowerCase()}.unit.plural` }),
                duration: PlanInterval.getCycle(product.billingInterval),
              }}
            />
          </Typography>
        </Box>
      );
    }

    const unitDiscountAmount = product.discountQuantity > 0
      ? product.discountAmount / product.discountQuantity
      : 0;
    const unitAmount = product.unitAmount - unitDiscountAmount;
    return (
      <Typography variant="subtitle1" fontWeight={500} fontSize={12} paddingBottom={1}>
        <FormattedMessage
          id="page.expand.plan.price.summary.product.details"
          values={{
            unitAmount: unitAmount.toLocaleString('en-us'),
            quantity: product.newQuantity,
            unit: intl.formatMessage({ id: `feature.${product.feature.toLowerCase()}.unit.plural` }),
            duration: PlanInterval.getCycle(product.billingInterval),
          }}
        />
      </Typography>
    );
  };
  return (
    <>
      {productsPreview?.length > 0
        ? productsPreview?.map(product => (
          <Box
            id={`expand-plan-price-summary-product-${product.feature.toLowerCase()}`}
            key={`expand-plan-price-summary-product-${product.feature.toLowerCase()}`}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            borderBottom="1px dashed #d6dae4"
          >
            <Box
              key={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-label`}
              id={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-label`}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="body1" fontWeight={700} fontSize={14} paddingY={1}>
                <FormattedMessage id={`feature.${product.feature.toLowerCase()}.product.name`} />
              </Typography>
              {renderProductPriceDetails(product)}
            </Box>
            <Box
              key={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-amount-summary`}
              id={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-amount-summary`}
              display="flex"
              flexDirection="column"
            >
              <Box
                key={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-amount`}
                id={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-amount`}
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                paddingY={1}
              >
                {product.discountAmount > 0 && (
                  <Typography
                    variant="subtitle1"
                    fontWeight={400}
                    fontSize={14}
                    paddingX={2}
                    sx={{
                      textDecoration: 'line-through',
                    }}
                  >
                    <FormattedMessage
                      id="page.expand.plan.price.summary.amount.display"
                      values={{ amount: product.subTotal.toLocaleString('en-us') }}
                    />
                  </Typography>
                )}
                <Typography variant="body1" fontWeight={700} fontSize={14}>
                  <FormattedMessage
                    id="page.expand.plan.price.summary.amount.display"
                    values={{ amount: (product.subTotal - product.discountAmount).toLocaleString('en-us') }}
                  />
                </Typography>
              </Box>
              {product.discountAmount > 0 && (
                <Box
                  key={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-saved-amount`}
                  id={`expand-plan-price-summary-product-${product.feature.toLowerCase()}-saved-amount`}
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  paddingBottom={1}
                >
                  <Chip
                    label={(
                      <Typography variant="body1" fontWeight={600} fontSize={10}>
                        <FormattedMessage
                          id="page.expand.plan.price.summary.discount.save.amount"
                          values={{ amount: product.discountAmount.toLocaleString('en-us') }}
                        />
                      </Typography>
                    )}
                    size="small"
                    variant="filled"
                    sx={theme => ({
                      backgroundColor: '#E8E5FF',
                      padding: theme.spacing(0.25, 1),
                    })}
                  />
                </Box>
              )}
            </Box>
          </Box>
        ))
        : (
          <Box
            id="expand-plan-price-summary-empty-product"
            display="flex"
            alignContent="center"
            alignItems="center"
            justifyContent="start"
            paddingBottom={3}
          >
            <Typography variant="body1" fontWeight={500} fontSize={14}>
              <FormattedMessage id="page.expand.plan.price.select.more" />
            </Typography>
          </Box>
        )}
    </>
  );
};
